import React, { useEffect, useState } from "react";
import { Dialog } from "@primer/react/drafts";
import {
  Box,
  Checkbox,
  Button,
  ButtonGroup,
  Spinner,
  Text,
  FormControl,
  TextInput,
  ActionList,
  ActionMenu,
} from "@primer/react";
import { SearchIcon, FilterIcon } from "@primer/octicons-react";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";
import DataTable from "../../components/ui/data-table";
import { useSelector } from "react-redux";
import { ex_kythi_monthi } from "../../models/response/kythi-monthi/ex_kythi_monthi";
import { monThiApi } from "../../api/monThiApi";
import { KyThiMonThiItemRequest } from "../../models/request/kythi-monthi/KyThiMonThiItemRequest";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import Modal from "../../components/ui/modal";

interface AddMonThiProps {
  ex_kythi_id: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface MonThi {
  id: number;
  mon_thi: string;
  mon_thi_en: string;
  thang_diem: string;
  nhan_xet_chung: boolean;
  dm_coso_id: number;
}

enum FilterType {
  ALL = 'all',
  HAS_COMMENT = 'has_comment'
}

const AddMonThi: React.FC<AddMonThiProps> = ({
  ex_kythi_id,
  onSuccess,
  onCancel
}) => {
  const { translate } = useCommonContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [monThis, setMonThis] = useState<MonThi[]>([]);
  const [filteredMonThis, setFilteredMonThis] = useState<MonThi[]>([]);
  const [selectedMonThiIds, setSelectedMonThiIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentFilter, setCurrentFilter] = useState<FilterType>(FilterType.ALL);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  // Load initial data
  const loadMonThis = async () => {
    try {
      setIsLoading(true);
      const res = await monThiApi.selectByNotInKyThi(ex_kythi_id);
      if (res.is_success) {
        const filteredData = res.data.filter((x: MonThi) => x.dm_coso_id === dm_coso_id);
        setMonThis(filteredData);
        setFilteredMonThis(filteredData);
      } else {
        NotifyHelper.Error(translate("Error.LoadDataFailed"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMonThis();
  }, [ex_kythi_id, dm_coso_id]);

  // Apply search and filters
  useEffect(() => {
    let filtered = [...monThis];

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        item => 
          item.mon_thi.toLowerCase().includes(query) ||
          item.mon_thi_en.toLowerCase().includes(query)
      );
    }

    // Apply filter type
    if (currentFilter === FilterType.HAS_COMMENT) {
      filtered = filtered.filter(item => item.nhan_xet_chung);
    }

    setFilteredMonThis(filtered);
  }, [searchQuery, currentFilter, monThis]);

  // Handle save
  const handleSave = async () => {
    if (selectedMonThiIds.length === 0) {
      NotifyHelper.Error(translate("Vui lòng chọn ít nhất một môn thi"));
      return;
    }

    try {
      setIsSaving(true);

      const baseData: ex_kythi_monthi = {
        id: 0,
        ex_kythi_id: ex_kythi_id,
        ex_monthi_id: 0,
        gio_bat_dau: null,
        gio_ket_thuc: null,
        thoi_luong_thi: null,
        ghi_chu: null,
        ex_hinhthucthi_id: 0,
        thu_tu: 0
      };

      const requestData: KyThiMonThiItemRequest = {
        ...baseData,
        list_monthi_id: selectedMonThiIds,
        gio_bat_dau_converted: '',
        gio_ket_thuc_converted: '',
        ngay_thi: null,
        ngay_thi_converted: '',
        thoi_luong_thi_converted: ''
      };

      const res = await monThiApi.insertMultiple(requestData);

      if (res.is_success) {
        NotifyHelper.Success(translate("Thêm môn thành công"));
        onSuccess();
      } else {
        NotifyHelper.Error(res.message || translate("Error.Generic"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.Generic"));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle selection
  const handleSelectMonThi = (id: number, checked: boolean) => {
    setSelectedMonThiIds(prev => {
      if (checked) {
        return [...prev, id];
      } else {
        return prev.filter(x => x !== id);
      }
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredMonThis.map(item => item.id);
      setSelectedMonThiIds(allIds);
    } else {
      setSelectedMonThiIds([]);
    }
  };

  return (
    <Modal
      isOpen
      title={translate("Thêm môn thi")}
      onClose={onCancel}
      width="100%"
    >
      <Box sx={{ p: 0}}>
        {/* Header with summary */}
        {/* <Box sx={{ mb: 3 }}>
          <Text as="p" sx={{ fontSize: 1, color: 'text.secondary' }}>
            {translate("Đã chọn")} {selectedMonThiIds.length} {translate("môn thi")}
          </Text>
        </Box> */}

        {/* Search and Filter Bar */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 1,
          alignItems: 'center',
          backgroundColor: 'canvas.subtle',
          p: 2,
          borderRadius: 2
        }}>
          <FormControl sx={{ flex: 1 }}>
            <TextInput
              leadingVisual={SearchIcon}
              placeholder={translate("Tìm kiếm môn thi...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              block
              contrast
            />
          </FormControl>
          
          <ActionMenu>
            <ActionMenu.Button variant="default">
              {translate("Lọc")}
            </ActionMenu.Button>
            <ActionMenu.Overlay>
              <ActionList selectionVariant="single">
                <ActionList.Item
                  selected={currentFilter === FilterType.ALL}
                  onSelect={() => setCurrentFilter(FilterType.ALL)}
                >
                  {translate("Tất cả môn thi")}
                </ActionList.Item>
                <ActionList.Item
                  selected={currentFilter === FilterType.HAS_COMMENT}
                  onSelect={() => setCurrentFilter(FilterType.HAS_COMMENT)}
                >
                  {translate("Môn có nhận xét chung")}
                </ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </Box>

        {/* Data Table */}
        <DataTable
          data={filteredMonThis}
          isLoading={isLoading}
          searchEnable={false}
          height="calc(100vh - 450px)" // Add this line to make it taller
          paging={{ enable: true, pageSize: 10, pageSizeItems: [10, 20, 50] }}
          columns={[
            {
              id: "selection",
              width: "50px",
              align: "center",
              headerRender: () => (
                <Checkbox
                  checked={selectedMonThiIds.length === filteredMonThis.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              ),
              cellRender: (data: MonThi) => (
                <Checkbox
                  checked={selectedMonThiIds.includes(data.id)}
                  onChange={(e) => handleSelectMonThi(data.id, e.target.checked)}
                />
              )
            },
            {
              dataField: "mon_thi",
              caption: translate("Môn thi")
            },
            {
              dataField: "mon_thi_en",
              caption: translate("Môn thi (En)")
            },
            {
              dataField: "thang_diem",
              caption: translate("Thang điểm"),
              width: "150px",
              align: "center"
            },
            {
              dataField: "nhan_xet_chung",
              caption: translate("Nhận xét chung"),
              width: "150px",
              align: "center",
              cellRender: (data: MonThi) => (
                <Box sx={{ 
                  color: data.nhan_xet_chung ? 'success.fg' : 'danger.fg',
                  fontWeight: 'bold'
                }}>
                  {data.nhan_xet_chung ? "✓" : "✕"}
                </Box>
              )
            }
          ]}
        />

        {/* Footer with Actions */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          borderTop: '1px solid',
          borderColor: 'border.default',
          mt: 1,
          pt: 3
        }}>
          <Text>
            {selectedMonThiIds.length > 0 && (
              <Text sx={{ fontSize: 1, color: 'text.secondary' }}>
                {translate("Đã chọn")} <Text sx={{ fontWeight: 'bold' }}>{selectedMonThiIds.length}</Text> {translate("môn thi")}
              </Text>
            )}
          </Text>
          <ButtonGroup>
            <Button onClick={onCancel}>
              {translate("Base.Label.Cancel")}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSave}
              disabled={isSaving || selectedMonThiIds.length === 0}
            >
              {isSaving && <Spinner size="small" sx={{ mr: 2 }} />}
              {translate("Base.Label.Save")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddMonThi;